import { invokeFunction } from "../api";
import { toApiRequestError } from "../errors";

export type PatientCommsStatusCode =
  | "scheduled"
  | "checked_in"
  | "pre_op"
  | "in_procedure"
  | "in_recovery"
  | "ready_for_pickup"
  | "discharged"
  | "delayed";

export interface PatientCommsContact {
  contact_id: string;
  relation_to_patient: string;
  display_name: string;
  phone: string | null;
  email: string | null;
  preferred_channel: string;
  consent_status: string;
  consented_at: string | null;
  consent_revoked_at: string | null;
  updated_at: string;
}

export interface PatientCommsStatusEvent {
  status_event_id: string;
  status_code: PatientCommsStatusCode;
  visibility: string;
  event_source: string;
  occurred_at: string;
  created_at: string;
}

export interface PatientCommsDispatch {
  dispatch_id: string;
  contact_id: string | null;
  status_event_id: string | null;
  channel: string;
  template_key: string;
  message_body: string;
  delivery_status: string;
  delivery_error_code: string | null;
  delivery_error_message: string | null;
  created_at: string;
}

export interface PatientCommsActiveShareLink {
  share_link_id: string;
  contact_id: string | null;
  expires_at: string;
  revoked_at: string | null;
  access_count: number;
  last_accessed_at: string | null;
}

export interface PatientCommsVisit {
  visit_id: string;
  facility_id: string;
  facility_name: string;
  visit_type: string;
  survey_date: string;
  visit_status: string;
  contacts: PatientCommsContact[];
  latest_status_event: PatientCommsStatusEvent | null;
  recent_status_events: PatientCommsStatusEvent[];
  recent_dispatches: PatientCommsDispatch[];
  active_share_link: PatientCommsActiveShareLink | null;
}

export interface PatientCommsBoardResponse {
  start_date: string;
  end_date: string;
  visits: PatientCommsVisit[];
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseContact(value: unknown): PatientCommsContact {
  if (!asRecord(value) || typeof value.contact_id !== "string") throw new Error("Malformed patient communications contact");
  return {
    contact_id: value.contact_id,
    relation_to_patient: typeof value.relation_to_patient === "string" ? value.relation_to_patient : "",
    display_name: typeof value.display_name === "string" ? value.display_name : "",
    phone: typeof value.phone === "string" ? value.phone : null,
    email: typeof value.email === "string" ? value.email : null,
    preferred_channel: typeof value.preferred_channel === "string" ? value.preferred_channel : "none",
    consent_status: typeof value.consent_status === "string" ? value.consent_status : "pending",
    consented_at: typeof value.consented_at === "string" ? value.consented_at : null,
    consent_revoked_at: typeof value.consent_revoked_at === "string" ? value.consent_revoked_at : null,
    updated_at: typeof value.updated_at === "string" ? value.updated_at : "",
  };
}

function parseStatusEvent(value: unknown): PatientCommsStatusEvent {
  if (!asRecord(value) || typeof value.status_event_id !== "string") throw new Error("Malformed patient communications status event");
  return {
    status_event_id: value.status_event_id,
    status_code: value.status_code as PatientCommsStatusCode,
    visibility: typeof value.visibility === "string" ? value.visibility : "shareable",
    event_source: typeof value.event_source === "string" ? value.event_source : "manual",
    occurred_at: typeof value.occurred_at === "string" ? value.occurred_at : "",
    created_at: typeof value.created_at === "string" ? value.created_at : "",
  };
}

function parseDispatch(value: unknown): PatientCommsDispatch {
  if (!asRecord(value) || typeof value.dispatch_id !== "string") throw new Error("Malformed patient communications dispatch");
  return {
    dispatch_id: value.dispatch_id,
    contact_id: typeof value.contact_id === "string" ? value.contact_id : null,
    status_event_id: typeof value.status_event_id === "string" ? value.status_event_id : null,
    channel: typeof value.channel === "string" ? value.channel : "",
    template_key: typeof value.template_key === "string" ? value.template_key : "",
    message_body: typeof value.message_body === "string" ? value.message_body : "",
    delivery_status: typeof value.delivery_status === "string" ? value.delivery_status : "queued",
    delivery_error_code: typeof value.delivery_error_code === "string" ? value.delivery_error_code : null,
    delivery_error_message: typeof value.delivery_error_message === "string" ? value.delivery_error_message : null,
    created_at: typeof value.created_at === "string" ? value.created_at : "",
  };
}

function parseVisit(value: unknown): PatientCommsVisit {
  if (!asRecord(value) || typeof value.visit_id !== "string") throw new Error("Malformed patient communications visit");
  return {
    visit_id: value.visit_id,
    facility_id: typeof value.facility_id === "string" ? value.facility_id : "",
    facility_name: typeof value.facility_name === "string" ? value.facility_name : "",
    visit_type: typeof value.visit_type === "string" ? value.visit_type : "",
    survey_date: typeof value.survey_date === "string" ? value.survey_date : "",
    visit_status: typeof value.visit_status === "string" ? value.visit_status : "",
    contacts: Array.isArray(value.contacts) ? value.contacts.map(parseContact) : [],
    latest_status_event: value.latest_status_event ? parseStatusEvent(value.latest_status_event) : null,
    recent_status_events: Array.isArray(value.recent_status_events) ? value.recent_status_events.map(parseStatusEvent) : [],
    recent_dispatches: Array.isArray(value.recent_dispatches) ? value.recent_dispatches.map(parseDispatch) : [],
    active_share_link:
      asRecord(value.active_share_link) && typeof value.active_share_link.share_link_id === "string"
        ? {
            share_link_id: value.active_share_link.share_link_id,
            contact_id: typeof value.active_share_link.contact_id === "string" ? value.active_share_link.contact_id : null,
            expires_at: typeof value.active_share_link.expires_at === "string" ? value.active_share_link.expires_at : "",
            revoked_at: typeof value.active_share_link.revoked_at === "string" ? value.active_share_link.revoked_at : null,
            access_count: typeof value.active_share_link.access_count === "number" ? value.active_share_link.access_count : 0,
            last_accessed_at: typeof value.active_share_link.last_accessed_at === "string" ? value.active_share_link.last_accessed_at : null,
          }
        : null,
  };
}

export async function getPatientCommsBoard(facilityId?: string): Promise<PatientCommsBoardResponse> {
  const query = facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : "";
  const { data, error } = await invokeFunction<unknown>("patient-comms", {
    method: "GET",
    path: `/${query}`,
  });
  if (error) throw toApiRequestError(error, "Failed to load patient communications board");
  if (!asRecord(data) || !Array.isArray(data.visits)) throw new Error("Malformed patient communications board response");
  return {
    start_date: typeof data.start_date === "string" ? data.start_date : "",
    end_date: typeof data.end_date === "string" ? data.end_date : "",
    visits: data.visits.map(parseVisit),
  };
}

export async function updatePatientCommsStatus(visitId: string, statusCode: PatientCommsStatusCode) {
  const { data, error } = await invokeFunction<unknown>("patient-comms", {
    method: "POST",
    path: `/${visitId}/status-update`,
    body: { status_code: statusCode, visibility: "shareable" },
  });
  if (error) throw toApiRequestError(error, "Failed to update patient communication status");
  return data;
}

export async function queuePatientCommsDispatch(visitId: string, statusCode: PatientCommsStatusCode, contactIds: string[]) {
  const { data, error } = await invokeFunction<unknown>("patient-comms", {
    method: "POST",
    path: `/${visitId}/dispatch`,
    body: { status_code: statusCode, contact_ids: contactIds },
  });
  if (error) throw toApiRequestError(error, "Failed to queue patient communication dispatch");
  return data;
}
